/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from '@hooks/useDebounce'
import { KeyValuePair, PaginationKeyValuePair } from '@utils/buckets/type'
import { ScheduleFilterSelectContext } from './ScheduleFilterSelectContext'
import {
  ScheduleFilterSelectContextType,
  ScheduleFilterSelectControllerProps,
} from './ScheduleFilterSelectTypes'

export function ScheduleFilterSelectController({
  filter,
  children,
  handleUpdateSearch,
  activeFilter,
  setActiveFilter,
}: ScheduleFilterSelectControllerProps): JSX.Element {
  // define references
  const block = useRef<boolean>(false)

  // define states
  const [page, setPage] = useState<number>(1)
  const [options, setOptions] = useState<KeyValuePair[]>([])
  const fieldName = filter.attr
  const [selecteds, setSelecteds] = useState(new Map<string, any>())
  const dispatchFilter = useDebounce(selecteds, 500)

  // define a function to append an options
  // always it is checked
  const handleChangeSelectOption = (
    values: {
      filter: string
      key?: string
    }[]
  ) => {
    const localSelecteds = new Map<string, string>(filter ? selecteds : null)
    values.forEach((v) => {
      localSelecteds.set(v.filter, v.key || '')
    })
    setSelecteds(localSelecteds)
  }

  // define a function to identify if
  // results is paginated or not, parse
  // it and then update state options
  const handleParseOptions = useCallback(
    (props: PaginationKeyValuePair | KeyValuePair[]) => {
      if (_.has(props, 'results')) {
        if (_.get(props, 'pagination.page') === 1) {
          setOptions(_.get(props, 'results', []))
        } else {
          setOptions(options.concat(_.get(props, 'results', [])))
        }
      } else if (props instanceof Array) {
        setOptions(props as KeyValuePair[])
      }
    },
    [options, page]
  )

  // define a function to load options
  // * if filter has pagination enabled
  // then pagination rules will be applied
  const handleLoadPage = (newPage: number, textSearch?: string) => {
    if (block.current === true || !filter.options) return
    setPage(newPage)
    block.current = true

    filter
      .options({
        text: filter.enabled_online_filter ? textSearch : undefined,
        limit: filter.enabled_pagination ? 10 : undefined,
        page: filter.enabled_pagination ? newPage || 1 : undefined,
      })
      .then(handleParseOptions)
      .finally(() => {
        block.current = false
      })
  }

  // define a function to emit an event to props
  // when a new filter is processed to be applied
  useEffect(() => {
    if (handleUpdateSearch) {
      handleUpdateSearch(Object.fromEntries(selecteds.entries()))
    }
  }, [dispatchFilter])

  // <!-- 1 - run the first fetch to load options -->
  // <!-- 2 - reset page always text changes -->
  useEffect(() => {
    handleLoadPage(1)
  }, [])

  // define accessible states provided to childrens
  const state: ScheduleFilterSelectContextType = useMemo(
    () => ({
      filter,
      fieldName,
      selecteds,
      options,
      handleChangeSelectOption,
      activeFilter,
      setActiveFilter,
    }),
    [
      filter,
      fieldName,
      selecteds,
      options,
      handleChangeSelectOption,
      activeFilter,
      setActiveFilter,
    ]
  )

  return (
    <ScheduleFilterSelectContext.Provider value={state}>
      {children}
    </ScheduleFilterSelectContext.Provider>
  )
}
